import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, ExternalLink, Clock, FileText, Search } from 'lucide-react';
import clsx from 'clsx';
import { useProjects, useCreateProject, useDeleteProject } from '../hooks/useSEO';
import type { SEOProject, CreateProjectRequest } from '@/shared/types';

export function ProjectsPage() {
  const navigate = useNavigate();
  const { projects, loading, error, refetch } = useProjects();
  const { createProject, loading: creating } = useCreateProject();
  const { deleteProject, loading: deleting } = useDeleteProject();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleCreate = async (data: CreateProjectRequest) => {
    try {
      const project = await createProject(data);
      setShowCreateModal(false);
      navigate(`/projects/${project.id}`);
    } catch {
      // Error is handled in the hook
    }
  };

  const handleDelete = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      setDeleteConfirm(null);
      refetch();
    } catch {
      // Error is handled in the hook
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">SEO Audits</h1>
          <p className="text-surface-500 mt-1">Manage your website SEO audit projects</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <Plus className="w-4 h-4" />
          New Audit
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
          {error}
        </div>
      )}

      {projects.length === 0 ? (
        <div className="card text-center py-12">
          <Search className="w-12 h-12 text-surface-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-900 mb-2">No audits yet</h3>
          <p className="text-surface-500 mb-6">
            Get started by creating your first SEO audit
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary mx-auto"
          >
            <Plus className="w-4 h-4" />
            Create Audit
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {projects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              onDelete={() => setDeleteConfirm(project.id)}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
          loading={creating}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <DeleteConfirmModal
          onClose={() => setDeleteConfirm(null)}
          onConfirm={() => handleDelete(deleteConfirm)}
          loading={deleting}
        />
      )}
    </div>
  );
}

function ProjectCard({
  project,
  onDelete,
}: {
  project: SEOProject;
  onDelete: () => void;
}) {
  const statusColors = {
    pending: 'badge-pending',
    crawling: 'badge-crawling',
    analyzing: 'badge-analyzing',
    completed: 'badge-completed',
    failed: 'badge-failed',
  };

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <Link to={`/projects/${project.id}`} className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-surface-900 hover:text-brand-600 transition-colors">
              {project.name}
            </h3>
            <span className={clsx('badge', statusColors[project.status])}>
              {project.status}
            </span>
          </div>

          <p className="text-sm text-surface-500 mt-1 flex items-center gap-1 truncate">
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
            {project.baseUrl}
          </p>

          <div className="flex items-center gap-4 mt-3 text-xs text-surface-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Created {new Date(project.createdAt).toLocaleDateString()}
            </span>
            {project.completedAt && (
              <span className="flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" />
                Completed {new Date(project.completedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </Link>

        <button
          onClick={(e) => {
            e.preventDefault();
            onDelete();
          }}
          className="p-2 text-surface-400 hover:text-red-600 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function CreateProjectModal({
  onClose,
  onCreate,
  loading,
}: {
  onClose: () => void;
  onCreate: (data: CreateProjectRequest) => void;
  loading: boolean;
}) {
  const [name, setName] = useState('');
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Project name is required');
      return;
    }

    if (!sitemapUrl.trim()) {
      setError('Sitemap URL is required');
      return;
    }

    try {
      new URL(sitemapUrl);
    } catch {
      setError('Invalid sitemap URL');
      return;
    }

    onCreate({ name: name.trim(), sitemapUrl: sitemapUrl.trim() });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold text-surface-900 mb-4">New SEO Audit</h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="label">Project Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Website Audit"
                className="input"
                autoFocus
              />
            </div>

            <div>
              <label className="label">Sitemap URL</label>
              <input
                type="url"
                value={sitemapUrl}
                onChange={(e) => setSitemapUrl(e.target.value)}
                placeholder="https://example.com/sitemap.xml"
                className="input"
              />
              <p className="text-xs text-surface-500 mt-1">
                Enter the full URL to your sitemap.xml file
              </p>
            </div>

            {error && (
              <div className="text-sm text-red-600">{error}</div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Audit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  onClose,
  onConfirm,
  loading,
}: {
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold text-surface-900 mb-2">Delete Audit?</h2>
        <p className="text-surface-600 mb-6">
          This will permanently delete the audit and all associated data. This action cannot be undone.
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="btn-danger"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
